/**
 * Histórico real de medição de espessura, montado a partir dos relatórios de
 * ME já emitidos — é isto que faz o API 570 parar de pedir espessuras e datas
 * digitadas à mão.
 *
 * Uma leitura = um ponto (CML) dentro de um relatório de ME, na data do
 * ensaio daquele relatório.
 */

import type { MeReport } from "./me-report-domain.js";

export interface ThicknessReading {
  /** Data do ensaio, "yyyy-mm-dd". */
  date: string;
  thicknessMm: number;
  reportId: string;
  reportNumber: string;
  /** Espessura mínima registrada para o ponto naquele relatório, quando houver. */
  minThicknessMm: number | null;
  nominalThicknessMm: number | null;
}

export interface CmlSummary {
  /** "ID DO PONTO" do relatório de ME. */
  id: string;
  component: string;
  location: string;
  readingCount: number;
  lastReadingDate: string;
}

export interface CmlThicknessHistory {
  cmlId: string;
  readings: ThicknessReading[];
  initial: ThicknessReading | null;
  previous: ThicknessReading | null;
  current: ThicknessReading | null;
  /** Menor espessura mínima registrada para o ponto, como referência de t requerida. */
  registeredMinimumThicknessMm: number | null;
  nominalThicknessMm: number | null;
  /** Menos de duas leituras: não há como calcular taxa de corrosão. */
  hasSufficientHistory: boolean;
}

/** Data do ensaio do relatório, com as quedas previstas do modelo. */
function reportDate(report: MeReport): string {
  const raw = report.testDate || report.periodEnd || report.periodStart || "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw));
  return match ? match[0] : "";
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Relatórios de ME do equipamento, do mais antigo para o mais recente. */
export function reportsForEquipment(reports: readonly MeReport[], equipmentId: string): MeReport[] {
  return reports
    .filter(r => r.equipmentId === equipmentId)
    .filter(r => reportDate(r) !== "")
    .sort((a, b) => reportDate(a).localeCompare(reportDate(b)));
}

/** Pontos (CML) já medidos no equipamento, para o seletor do API 570. */
export function listCmlsForEquipment(reports: readonly MeReport[], equipmentId: string): CmlSummary[] {
  const summaries = new Map<string, CmlSummary>();

  for (const report of reportsForEquipment(reports, equipmentId)) {
    const date = reportDate(report);
    for (const point of report.points || []) {
      const id = String(point.id || "").trim();
      if (!id) continue;
      if (toNumber(point.actualThickness) === null) continue;

      const existing = summaries.get(id);
      if (existing) {
        existing.readingCount += 1;
        existing.lastReadingDate = date;
        if (point.component) existing.component = point.component;
        if (point.location) existing.location = point.location;
      } else {
        summaries.set(id, {
          id,
          component: point.component || "",
          location: point.location || "",
          readingCount: 1,
          lastReadingDate: date
        });
      }
    }
  }

  return Array.from(summaries.values()).sort((a, b) => a.id.localeCompare(b.id, "pt-BR", { numeric: true }));
}

/**
 * Leituras de UM ponto ao longo do tempo. A inicial é a primeira medição
 * registrada, a anterior é a penúltima e a atual é a mais recente — nada é
 * digitado pelo usuário.
 */
export function buildCmlThicknessHistory(
  reports: readonly MeReport[],
  equipmentId: string,
  cmlId: string
): CmlThicknessHistory {
  const readings: ThicknessReading[] = [];
  let registeredMinimum: number | null = null;
  let nominal: number | null = null;

  for (const report of reportsForEquipment(reports, equipmentId)) {
    const date = reportDate(report);
    for (const point of report.points || []) {
      if (String(point.id || "").trim() !== cmlId) continue;

      const thickness = toNumber(point.actualThickness);
      const min = toNumber(point.minThickness);
      const nom = toNumber(point.nominalThickness);

      if (min !== null) registeredMinimum = registeredMinimum === null ? min : Math.min(registeredMinimum, min);
      if (nom !== null) nominal = nom;

      if (thickness === null) continue;
      readings.push({
        date,
        thicknessMm: thickness,
        reportId: String(report.id),
        reportNumber: report.reportNumber || "",
        minThicknessMm: min,
        nominalThicknessMm: nom
      });
    }
  }

  readings.sort((a, b) => a.date.localeCompare(b.date));

  const current = readings.length > 0 ? readings[readings.length - 1]! : null;
  const previous = readings.length > 1 ? readings[readings.length - 2]! : null;
  const initial = readings.length > 1 ? readings[0]! : null;

  return {
    cmlId,
    readings,
    initial,
    previous,
    current,
    registeredMinimumThicknessMm: registeredMinimum,
    nominalThicknessMm: nominal,
    hasSufficientHistory: readings.length >= 2
  };
}

export const INSUFFICIENT_HISTORY_MESSAGE =
  "HISTÓRICO INSUFICIENTE PARA CÁLCULO DA TAXA DE CORROSÃO.";
